import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ───────────────────────────────────────────────────────────────────────────
// Phase G-Security HOTFIX (27.05.2026) — Auth-Transport Feature Flag
//
// Background: The Emergent / Cloudflare ingress rewrites our backend's
// `Access-Control-Allow-Origin: <origin>` header to `*` while leaving
// `Access-Control-Allow-Credentials: true` intact. Per the CORS spec, this
// header combination is INVALID, and browsers therefore (a) refuse to expose
// the response body to JS and (b) DROP every Set-Cookie from the response.
// Result with cookie-only auth: every /auth/me returns 401 → user bounces
// to /login?expired=1 immediately after a successful login.
//
// Current setting: `REACT_APP_USE_COOKIE_AUTH` env var, default `false`.
// When the platform CORS is corrected (single specific Origin instead of `*`),
// flip `REACT_APP_USE_COOKIE_AUTH=true` in frontend/.env, rebuild, and auth
// will go back to httpOnly cookies WITHOUT any code change — the request
// interceptor will skip the Bearer header, axios will send/receive cookies,
// and AuthContext.login() will skip the localStorage write.
//
// Use this flag responsibly: enabling it before the ingress fix WILL break
// auth for every user. Validate by inspecting `/api/auth/login` response
// headers for `Access-Control-Allow-Origin: https://<your-domain>` (NOT `*`).
// ───────────────────────────────────────────────────────────────────────────
export const USE_COOKIE_AUTH = (process.env.REACT_APP_USE_COOKIE_AUTH || "false")
  .toLowerCase() === "true";

export const api = axios.create({
  baseURL: API,
  // `withCredentials` is what enables cookie send/receive over CORS. We tie
  // it 1:1 to the feature flag so a single env var flip switches transport.
  withCredentials: USE_COOKIE_AUTH,
});

export const TOKEN_KEY = "mt_token";

// Request interceptor: attach Bearer header ONLY in Bearer mode. In cookie
// mode the browser handles auth via httpOnly cookies (no JS access needed).
api.interceptors.request.use((config) => {
  if (USE_COOKIE_AUTH) return config;
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (_) { /* private mode etc. */ }
  return config;
});

// Kept for back-compat with AuthContext (previous v3 grace-period defense).
// No-op now that cookie auth is disabled — Bearer auth doesn't have the race
// condition that the grace period was guarding against.
export function markLoggedInNow() { /* no-op */ }

// Graceful 401 handling: drop expired token, redirect to /login.
let _redirecting = false;
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login");
    if (status === 401 && !isAuthProbe) {
      if (!USE_COOKIE_AUTH) {
        // Bearer mode: clear stale token from storage so the next request
        // doesn't carry it. Cookie mode: the server has already invalidated
        // the cookie via Set-Cookie or it has expired — nothing for us to do.
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
      }
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        if (!_redirecting) {
          _redirecting = true;
          setTimeout(() => { window.location.assign("/login?expired=1"); }, 50);
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
